// Dashboard GraphQL hooks (stats, critical tickets, recent activity, time summary, task stats).
// User search hooks live in use-search-users.ts.
//
// These five hooks no longer each fire their own browser Apollo query. They all
// read from ONE cached server route via `useDashboardSummary()` (same TanStack
// queryKey, so a dashboard page makes a single request), then run the SAME
// processing they always did over the raw payload. Server-side cache: 5-min TTL,
// per-user, RLS-scoped — see lib/cache/dashboard-cache.ts + /api/dashboard/summary.

import { useMemo } from "react";
import { format, startOfMonth, startOfWeek } from "date-fns";
import { parseBigFloat } from "@wtc/ui";
import { processTicketsData } from "@/lib/utils/crm/ticket-helpers";
import type { ProcessedTicketData, RawTicketData } from "@/lib/utils/crm/ticket-helpers";
import { standardizeTimeEntry } from "@/lib/utils/crm/time-entry-helpers";
import type { StandardTimeEntry } from "@/lib/utils/crm/time-entry-helpers";
import { RecentActivityItem, TimeSummary } from "@/lib/types/unified";
import { useDashboardSummary } from "./use-dashboard-summary";

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/** Map a processed ticket to a RecentActivityItem. */
function ticketToActivity(ticket: ProcessedTicketData): RecentActivityItem {
  const ticketNumber = ticket.formatted_ticket_number || ticket.ticket_number;
  return {
    id: ticket.id,
    type: "ticket" as const,
    title: "New Ticket Created",
    description: `${ticketNumber ?? ""} - ${ticket.title ?? ""}`,
    // The recent-activity ticket query does not join the creator, so there is
    // no email to show — leave `user` unset rather than render a raw UUID.
    // `created_by_email` flows through the loose RawTicketData index signature,
    // so narrow it to a string before assigning to the typed `user` field.
    user:
      typeof ticket.created_by_email === "string"
        ? ticket.created_by_email
        : undefined,
    timestamp: ticket.created_at,
    ticket_id: ticket.id,
    ticket_number: ticketNumber ? String(ticketNumber) : undefined,
    ticket_title: ticket.title,
    status: ticket.status,
    priority: ticket.priority,
    created_at: ticket.created_at,
    updated_at: ticket.updated_at ?? ticket.created_at,
    assigned_to: ticket.assigned_to,
    assigned_user: ticket.assigned_user,
    assigned_to_name: ticket.assigned_to_name,
    client_name: ticket.client_name,
    client_email: ticket.client_email,
  };
}

/**
 * Normalize raw GraphQL ticket edges and run `processTicketsData`.
 *
 * The three dashboard ticket queries each select a different field subset and
 * codegen marks every column `Maybe<>`, so the edge node can't be statically
 * typed as `RawTicketData` (which requires id/title/status/…). This one cast is
 * the deliberate codegen→processing boundary; everything downstream
 * (`ProcessedTicketData`, `ticketToActivity`) is fully typed.
 */
function processTicketEdges(
  edges: ReadonlyArray<{ node?: Record<string, unknown> | null } | null> | undefined,
): ProcessedTicketData[] {
  const nodes = (edges ?? [])
    .map((edge) => edge?.node)
    .filter((node): node is Record<string, unknown> => node != null)
    .map((t) => ({
      ...t,
      status: (typeof t.status === "string" && t.status) || "unknown",
      priority: (typeof t.priority === "string" && t.priority) || "medium",
    }));
  return processTicketsData(nodes as unknown as RawTicketData[]);
}

// ---------------------------------------------------------------------------
// Time-summary bucketing
// ---------------------------------------------------------------------------

interface TimeBucket {
  hours: number;
  tickets: number;
  revenue: number;
}

/**
 * Aggregate standardized time entries into a {hours, tickets, revenue} bucket.
 * Revenue is summed per-entry (billable hours × that entry's rate) rather than
 * via an averaged rate, so a mix of rates totals correctly. BigFloat values are
 * parsed through `parseBigFloat` — never `Number(string)`.
 */
function bucketTimeEntries(entries: StandardTimeEntry[]): TimeBucket {
  let hours = 0;
  let revenue = 0;
  for (const entry of entries) {
    const entryHours = parseBigFloat(entry.hours) ?? 0;
    hours += entryHours;
    if (entry.entry_type === "billable") {
      revenue += entryHours * (parseBigFloat(entry.billable_rate) ?? 0);
    }
  }
  return { hours, tickets: entries.length, revenue };
}

const EMPTY_TIME_SUMMARY: TimeSummary = {
  today: { hours: 0, revenue: 0, tickets: 0 },
  thisWeek: { hours: 0, revenue: 0, tickets: 0 },
  thisMonth: { hours: 0, revenue: 0, tickets: 0 },
};

// -- Dashboard Statistics ----------------------------------------------------

export function useDashboardStatsGraphQL() {
  const { data: summary, isLoading, error, refetch } = useDashboardSummary();
  const stats = summary.stats;

  return useMemo(() => {
    if (!stats) {
      return {
        recentActivity: [] as RecentActivityItem[],
        timeSummary: {
          today: { hours: 0, tickets: 0, revenue: 0 },
          thisWeek: { hours: 0, tickets: 0, revenue: 0 },
          thisMonth: { hours: 0, tickets: 0, revenue: 0 },
        } as TimeSummary,
        isLoading,
        error,
        refetch,
      };
    }

    const processedTickets = processTicketEdges(stats.ticketsCollection?.edges || []);
    const recentActivity: RecentActivityItem[] = processedTickets.slice(0, 10).map(ticketToActivity);

    const timeEntries = (stats.time_entriesCollection?.edges || [])
      .map((edge) => edge?.node)
      .filter((node): node is NonNullable<typeof node> => node != null)
      .map(standardizeTimeEntry);

    const today = new Date().toISOString().split("T")[0];
    const todayEntries = timeEntries.filter((e) => e.date_worked === today);
    const totalHours = todayEntries.reduce((sum, e) => sum + (parseBigFloat(e.hours) ?? 0), 0);
    const billableHours = todayEntries
      .filter((e) => e.entry_type === "billable")
      .reduce((sum, e) => sum + (parseBigFloat(e.hours) ?? 0), 0);
    const ratedEntries = todayEntries.filter((e) => e.billable_rate);
    const averageRate =
      ratedEntries.reduce((sum, e) => sum + (parseBigFloat(e.billable_rate) ?? 0), 0) /
      Math.max(ratedEntries.length, 1);

    const timeSummary: TimeSummary = {
      today: { hours: totalHours, revenue: billableHours * averageRate, tickets: todayEntries.length },
      thisWeek: { hours: 0, tickets: 0, revenue: 0 },
      thisMonth: { hours: 0, tickets: 0, revenue: 0 },
    };

    return { recentActivity, timeSummary, isLoading, error, refetch };
  }, [stats, isLoading, error, refetch]);
}

// -- Critical Tickets --------------------------------------------------------

export function useCriticalTicketsGraphQL() {
  const { data: summary, isLoading, error, refetch } = useDashboardSummary();
  const edges = summary.criticalTickets?.ticketsCollection?.edges;

  return useMemo(
    () => ({
      data: edges ? processTicketEdges(edges) : [],
      isLoading,
      error,
      refetch,
    }),
    [edges, isLoading, error, refetch],
  );
}

// -- Recent Activity ---------------------------------------------------------

export function useRecentActivityGraphQL() {
  const { data: summary, isLoading, error, refetch } = useDashboardSummary();
  const edges = summary.recentActivity?.ticketsCollection?.edges;

  return useMemo(() => {
    // Tickets ONLY. Time entries are merged in by the card's combiner from
    // `useTimeEntries` (which joins ticket + user), so emitting them here too
    // produced duplicate rows — and this query doesn't join those relations, so
    // its time-entry rows rendered "Unknown" / "No Ticket". Tickets are the one
    // activity type this query can fully resolve.
    const activities: RecentActivityItem[] = (edges ? processTicketEdges(edges) : [])
      .slice(0, 10)
      .map(ticketToActivity);

    const sorted = activities
      .slice()
      .sort((a, b) => new Date(b.timestamp || "").getTime() - new Date(a.timestamp || "").getTime());

    return { data: sorted, isLoading, error, refetch };
  }, [edges, isLoading, error, refetch]);
}

// -- Time Summary ------------------------------------------------------------

export function useTimeSummaryGraphQL() {
  const { data: summary, isLoading, error, refetch } = useDashboardSummary();
  const edges = summary.timeSummary?.time_entriesCollection?.edges;

  // Period boundaries as YYYY-MM-DD strings (date_worked is a date column, so
  // string comparison buckets correctly). The server fetches a wide enough range
  // (from the earlier of week-start / month-start); we bucket today / this-week /
  // this-month here against the viewer's local dates.
  const { todayStr, startOfWeekStr, startOfMonthStr } = useMemo(() => {
    const now = new Date();
    return {
      todayStr: format(now, "yyyy-MM-dd"),
      startOfWeekStr: format(startOfWeek(now, { weekStartsOn: 1 }), "yyyy-MM-dd"),
      startOfMonthStr: format(startOfMonth(now), "yyyy-MM-dd"),
    };
  }, []);

  return useMemo(() => {
    if (!edges) {
      return { data: EMPTY_TIME_SUMMARY, isLoading, error, refetch };
    }

    const entries = edges
      .map((edge) => edge?.node)
      .filter((node): node is NonNullable<typeof node> => node != null)
      .map(standardizeTimeEntry);

    const timeSummary: TimeSummary = {
      today: bucketTimeEntries(entries.filter((e) => e.date_worked === todayStr)),
      thisWeek: bucketTimeEntries(entries.filter((e) => e.date_worked >= startOfWeekStr)),
      thisMonth: bucketTimeEntries(entries.filter((e) => e.date_worked >= startOfMonthStr)),
    };

    return { data: timeSummary, isLoading, error, refetch };
  }, [edges, isLoading, error, refetch, todayStr, startOfWeekStr, startOfMonthStr]);
}

// -- User Task Statistics ----------------------------------------------------

// NOTE: the cached summary is always scoped to the authenticated user. Every call
// site passes the current user's id, so the `userId` arg is kept only for
// signature compatibility; stats reflect the signed-in user.
export function useUserTaskStatsGraphQL(_userId: string) {
  const { data: summary, isLoading, error, refetch } = useDashboardSummary();
  const edges = summary.taskStats?.contact_tasksCollection?.edges;

  return useMemo(() => {
    if (!edges) {
      return {
        data: { pending: 0, inProgress: 0, overdue: 0, total: 0 },
        isLoading,
        error,
        refetch,
      };
    }

    const tasks = edges
      .map((edge) => edge?.node)
      .filter((node): node is NonNullable<typeof node> => node != null);

    const now = new Date();
    return {
      data: {
        pending: tasks.filter((t) => t.status === "pending").length,
        inProgress: tasks.filter((t) => t.status === "in_progress").length,
        overdue: tasks.filter((t) => t.due_date && new Date(t.due_date) < now && t.status !== "completed").length,
        total: tasks.length,
      },
      isLoading,
      error,
      refetch,
    };
  }, [edges, isLoading, error, refetch]);
}

// Re-exports for backward compatibility — user search hooks live in use-search-users.ts
export { useSearchUsersGraphQL, useFetchMoreUsers, useSearchEmployeesFTS } from "./use-search-users";
